import React, { useEffect, useRef } from "react";

import { ReactComponent as HomeSmile } from "../../assets/icons/home-smile.svg";
import type { MentionedAsset } from "../../hooks/useMentionableAssets";
import AssetImage from "../AssetImage";

interface MentionChipInnerOpts {
  size: number;
  groupNameClass: string;
  nameClass?: string;
  fallbackLabel?: string;
}

// The picture, group and name that every asset row and mention chip shares
export function renderMentionChipInner(
  asset: { name: string; imageUrl?: string } | undefined,
  groupName: string | undefined,
  opts: MentionChipInnerOpts
): React.ReactNode {
  return (
    <>
      <AssetImage
        asset={asset}
        assetName={asset?.name}
        size={opts.size}
        className="shrink-0 !rounded-full !border-0"
        fallbackIcon={<HomeSmile className={opts.size <= 16 ? "h-3 w-3" : "h-4 w-4"} />}
      />
      {groupName && <span className={opts.groupNameClass}>{groupName} /</span>}
      <span className={opts.nameClass}>{asset?.name ?? opts.fallbackLabel}</span>
    </>
  );
}

interface AssetPickerListProps {
  assets: MentionedAsset[];
  /** Controlled highlight, so the owner can drive it from whichever element has keyboard focus */
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (asset: MentionedAsset) => void;
  /** Hovered row and its element (null on leave) — the chat picker positions a description tooltip from it */
  onHoverItem?: (asset: MentionedAsset | null, element: HTMLLIElement | null) => void;
  /** Rows that are listed but not selectable, each tagged with disabledLabel */
  disabledIds?: Set<string>;
  disabledLabel?: string;
  emptyLabel: React.ReactNode;
  /** The "⇥ tab" affordance on the active row, for pickers driven from a text editor */
  tabHint?: boolean;
  className?: string;
}

// The asset results list the AI Analyst's @-mention picker draws: image, group, name, with the active row highlighted and scrolled into view. Shared with every other asset search so they all look and move the same.
export const AssetPickerList: React.FC<AssetPickerListProps> = ({
  assets,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  onHoverItem,
  disabledIds,
  disabledLabel = "Added",
  emptyLabel,
  tabHint = false,
  className = "",
}) => {
  const listRef = useRef<HTMLUListElement>(null);

  // Scroll the active item into view whenever activeIndex changes
  useEffect(() => {
    if (!listRef.current) return;
    const activeItem = listRef.current.querySelectorAll('[role="option"]')[activeIndex] as HTMLElement | undefined;
    activeItem?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <ul
      ref={listRef}
      className={`eai-card overflow-hidden overflow-y-auto p-1 ${className}`}
      role="listbox"
      aria-label="Asset suggestions"
      onMouseLeave={() => onHoverItem?.(null, null)}
    >
      {assets.length === 0 ? (
        <li className="px-3 py-2 text-sm text-eai-ink-subtle">{emptyLabel}</li>
      ) : (
        assets.map((asset, index) => {
          const disabled = disabledIds?.has(asset.id) ?? false;
          const active = index === activeIndex;
          return (
            <li
              key={asset.id}
              role="option"
              aria-selected={active}
              aria-disabled={disabled || undefined}
              className={`flex items-center gap-2 rounded-full px-2 py-1.5 text-sm transition-colors ${
                disabled
                  ? "cursor-not-allowed text-eai-ink-disabled"
                  : active
                  ? "cursor-pointer bg-eai-surface-hover text-eai-ink"
                  : "cursor-pointer text-eai-ink-soft hover:bg-eai-surface-sunken"
              }`}
              onMouseEnter={(e) => {
                onActiveIndexChange(index);
                onHoverItem?.(asset, e.currentTarget);
              }}
              onMouseDown={(e) => {
                // Select on mousedown so the element that owns the keyboard keeps its focus
                e.preventDefault();
                if (!disabled) onSelect(asset);
              }}
            >
              {renderMentionChipInner(asset, asset.groupName, {
                size: 24,
                groupNameClass: "text-eai-ink-faint",
                nameClass: "font-medium",
              })}
              {asset.kind === "group" && (
                <span className="shrink-0 rounded border border-eai-border px-1 py-px text-eai-caption uppercase tracking-wide text-eai-ink-faint">
                  Group
                </span>
              )}
              {disabled && <span className="eai-tag-pill-sm ml-auto shrink-0">{disabledLabel}</span>}
              {tabHint && active && !disabled && (
                <span className="ml-auto hidden shrink-0 items-center gap-0.5 rounded border border-eai-border px-1 py-px text-eai-caption text-eai-ink-faint sm:inline-flex">
                  ⇥ tab
                </span>
              )}
            </li>
          );
        })
      )}
    </ul>
  );
};

export default AssetPickerList;
